/*
 * Copyright (C) 2020 Airfordable, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { graphql } from 'gatsby';
import { IGatsbyImageData, IImage } from 'gatsby-plugin-image';
import React from 'react';

import {
  FooterCol,
  FooterColSocial,
  FooterContainer,
  FooterCopyright,
  FooterNavigation,
  FooterSocial,
  FooterText,
  FooterTitle,
  PreFooterContent,
  PreFooterImage,
  PreFooterImageWrap,
  PreFooterTitle,
  PreFooterWrapper,
} from './FooterCommon';
import { FooterDesktop, FooterDesktopText } from './FooterDesktop';
import { FooterMobile } from './FooterMobile';
import { Overflow } from 'components/partials/Overflow';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Actionable,
  Button,
} from 'components/UI';
import { IconLogo } from 'components/UI';

export interface FooterLinksProps {
  link: string;
  text: string;
}

export interface FooterNavListProps {
  title: string;
  list: FooterLinksProps[];
}

export interface FooterProps {
  linkInstance: React.ElementType;
  menu: FooterNavListProps[];
  social: FooterLinksProps[];
  copyright: string;
  footerNote: string;
  preFooter: {
    title: string;
    linkText: string;
    linkUrl: string;
    images: {
      asset: {
        childImageSharp: {
          gatsbyImageData: IGatsbyImageData;
          original: IImage;
        };
      };
      main: { childImageSharp: { gatsbyImageData: IGatsbyImageData } };
    }[];
  };
}

const Text: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    // @ts-ignore: Due to missing types in Material UI for newer versions of typescript.
    <FooterText component="p" variant="body2">
      {children}
    </FooterText>
  );
};

const Title: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    // @ts-ignore: Due to missing types in Material UI for newer versions of typescript.
    <FooterTitle component="p" variant="body2">
      {children}
    </FooterTitle>
  );
};

const Footer: React.FC<FooterProps> = ({
  linkInstance,
  menu,
  social,
  copyright,
  footerNote,
  preFooter,
}) => {
  const getDate = new Date();
  const currentYear = getDate.getFullYear();

  return (
    <Overflow component="footer">
      <PreFooterWrapper>
        {preFooter.images.map((img, index) => (
          <PreFooterImageWrap
            className="pre-footer-image"
            key={`pre-footer-image-${index}`}
          >
            <PreFooterImage
              alt=""
              image={img.main.childImageSharp.gatsbyImageData}
              asset={img.asset?.childImageSharp?.original.src}
              assetAspectRatio={
                img.asset
                  ? img.asset?.childImageSharp?.original.width /
                    img.asset?.childImageSharp?.original.height
                  : undefined
              }
            />
          </PreFooterImageWrap>
        ))}

        <PreFooterContent>
          <div>
            <PreFooterTitle as="h2">{preFooter.title}</PreFooterTitle>
            <Button to={preFooter.linkUrl}>{preFooter.linkText}</Button>
          </div>
        </PreFooterContent>
      </PreFooterWrapper>

      <FooterNavigation>
        <FooterContainer>
          <FooterCol md={3} direction="column" container>
            <Actionable component={linkInstance} to="/">
              <IconLogo mode="light" />
            </Actionable>
            <FooterCopyright>
              <Text>{footerNote}</Text>
              <Text>
                © {currentYear} {copyright}
              </Text>
            </FooterCopyright>
          </FooterCol>
          <FooterCol md={5}>
            <FooterDesktop>
              {menu?.map(({ title, list }, index) => {
                return (
                  <div key={`${title}-${index}`}>
                    <Title>{title}</Title>
                    {list?.map(({ link, text }, index) => {
                      return (
                        <FooterDesktopText key={`${text}-${index}`}>
                          <Actionable to={link} component={linkInstance}>
                            {text}
                          </Actionable>
                        </FooterDesktopText>
                      );
                    })}
                  </div>
                );
              })}
            </FooterDesktop>

            <FooterMobile>
              {menu?.map(({ title, list }, index) => {
                return (
                  <Accordion variant="light" key={`${title}-${index}`}>
                    <AccordionSummary>
                      <Title>{title}</Title>
                    </AccordionSummary>
                    {list?.map(({ link, text }, index) => {
                      return (
                        <AccordionDetails key={`${text}-${index}`}>
                          <FooterDesktopText>
                            <Actionable to={link} component={linkInstance}>
                              {text}
                            </Actionable>
                          </FooterDesktopText>
                        </AccordionDetails>
                      );
                    })}
                  </Accordion>
                );
              })}
            </FooterMobile>
          </FooterCol>
          <FooterColSocial md={4}>
            <FooterSocial>
              {social?.map(({ link, text }, index) => (
                <FooterDesktopText
                  className="footer-desktop__text-social"
                  key={`${link}-${text}-${index}`}
                >
                  <Actionable to={link} component={linkInstance}>
                    {text}
                  </Actionable>
                </FooterDesktopText>
              ))}
            </FooterSocial>
          </FooterColSocial>
        </FooterContainer>
      </FooterNavigation>
    </Overflow>
  );
};

const query = graphql`
  fragment Footer on DefaultYamlNavigation {
    footer {
      preFooter {
        title
        linkText
        linkUrl
        images {
          main {
            childImageSharp {
              gatsbyImageData
            }
          }
          asset {
            childImageSharp {
              original {
                width
                height
                src
              }
            }
          }
        }
      }
      footerNote
      copyright
      menu {
        title
        list {
          link
          text
        }
      }
      social {
        link
        text
      }
    }
  }
`;

export { Footer, query };
